import { computed, nextTick, ref, watch, onScopeDispose } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import { useLocale } from './useLocale';
import { fetchMaluku, createMalukuCollection } from '@/api/malukuClient.mjs';
import { applyGenericSeo, applyBreadcrumbSeo, buildCanonicalUrl, SEO_ENTITY_IDS } from '@/utils/seoUtils';

export function useMalukuRequest(endpoint, { collection = false, fetcher = fetchMaluku } = {}) {
    const data = ref(null);
    const loading = ref(false);
    const error = ref(null);
    let controller;
    let cursor;
    let generation = 0;
    async function loadMore() {
        if (!cursor || loading.value || data.value?.complete) return;
        const current = generation; const request = cursor;
        loading.value = true; error.value = null;
        try {
            const result = await request.loadNext();
            if (current === generation) {
                // Browser scroll anchoring can otherwise follow the sentinel
                // down after an append and immediately request every page.
                const positions = data.value ? [document.scrollingElement, ...document.querySelectorAll('.maluku-panel-body')]
                    .filter(Boolean).map(element => ({ element, top: element.scrollTop, left: element.scrollLeft })) : [];
                data.value = result;
                await nextTick();
                if (current === generation) for (const { element, top, left } of positions) {
                    if (element.isConnected) element.scrollTo({ top, left, behavior: 'instant' });
                }
            }
        } catch (failure) {
            if (current === generation && failure.name !== 'AbortError') error.value = failure;
        } finally {
            if (current === generation) loading.value = false;
        }
    }
    async function reload() {
        const current = ++generation;
        controller?.abort();
        const path = endpoint();
        data.value = null;
        error.value = null;
        loading.value = false; cursor = null;
        if (!path) return;
        controller = new AbortController();
        if (collection) {
            cursor = createMalukuCollection(path, { signal: controller.signal });
            data.value = cursor.data;
            if (!data.value) await loadMore();
            return;
        }
        loading.value = true;
        try {
            const result = await fetcher(path, { signal: controller.signal });
            if (current === generation) data.value = result;
        } catch (failure) {
            if (current === generation && failure.name !== 'AbortError') error.value = failure;
        } finally {
            if (current === generation) loading.value = false;
        }
    }
    watch(endpoint, reload, { immediate: true });
    onScopeDispose(() => { generation++; controller?.abort(); });
    const hasMore = computed(() => collection && Boolean(data.value) && !data.value.complete);
    return { data, loading, error, reload, loadMore, hasMore };
}

export function useMalukuSeo(title, description) {
    const route = useRoute();
    const { locale, localePath } = useLocale();
    const canonicalRoute = computed(() => route.path.replace(/^\/(?:nl|en|id)(?=\/|$)/, '').replace(/\/+$/, '') || '/');
    const canonicalUrl = computed(() => buildCanonicalUrl({ currentLocale: locale.value, route: canonicalRoute.value }));
    useHead(computed(() => ({
        script: canonicalRoute.value === '/maluku' ? [{
            key: 'maluku-collection',
            type: 'application/ld+json',
            children: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                '@id': `${canonicalUrl.value}#webpage`,
                url: canonicalUrl.value,
                name: title(),
                description: description(),
                inLanguage: locale.value,
                isPartOf: { '@id': SEO_ENTITY_IDS.website },
                publisher: { '@id': SEO_ENTITY_IDS.organization },
            }),
        }] : [],
    })));
    watch([title, description, () => route.path, locale], ([pageTitle, pageDescription]) => {
        applyGenericSeo({
            title: `${pageTitle} | Sejarah Thümann`, description: pageDescription,
            image: 'https://sejarah-thumann.online/og-image.jpg',
            currentLocale: locale.value,
            route: canonicalRoute.value,
        }, { includeHreflangs: true, includeTwitter: true });
        applyBreadcrumbSeo([
            { name: 'Sejarah Thümann', url: `https://sejarah-thumann.online${localePath('/')}` },
            { name: 'Maluku', url: `https://sejarah-thumann.online${localePath('/maluku')}` },
            ...(canonicalRoute.value === '/maluku' ? [] :
                [{ name: pageTitle, url: canonicalUrl.value }]),
        ]);
    }, { immediate: true });
}
